import { LAST_SPACE_ID_KEY, SYNC_CONFIG_KEY, SyncMode } from './constants';

// The decision values are needed by the app before the rest of the sync code is loaded.
export { ForeignLocalDataDecision, ForeignLocalDataReason } from './constants';

/**
 * Device-local sync configuration, stored as JSON under `wiztime.sync` (data dictionary section 7).
 * It never leaves the device and holds no secrets: the keys live in IndexedDB (`key-store.ts`).
 */
export interface SyncConfig {
  /** Server origin; empty for the page's own origin. */
  readonly serverUrl: string;
  readonly spaceId: string;
  /** Highest server sequence number whose records have been merged into the local store. */
  readonly cursor: number;
  /**
   * Outbox watermark: every record this device authored with an hlc at or below it is on the server.
   * Empty before the first push.
   */
  readonly lastPushedHlc: string;
  /**
   * `null` while only this device's new records need pushing. Otherwise a full push is in progress:
   * every record (whoever authored it) with an hlc above this value still has to be pushed. It
   * starts as '' when sync is enabled, after an import, when the user uploads the data again after the
   * space was deleted, and when the space or the local database was replaced (`epoch`, `dbInstanceId`).
   */
  readonly fullPushFrom: string | null;
  /** Epoch milliseconds of the last successful sync. */
  readonly lastSyncAt: number | null;
  /**
   * The server's identifier of the incarnation of the space this cursor belongs to (16 hex digits).
   * A different value in a server answer means the space was deleted and created again, so the
   * cursor is meaningless and everything starts over. '' while unknown (configurations written
   * before the field existed): the first answer is adopted without a reset.
   */
  readonly epoch: string;
  /**
   * Identity of the local database this configuration was synced with (`Store.getDbInstanceId`).
   * A different value means the database was deleted or replaced while this configuration
   * survived, so the cursor refers to data the database no longer holds. '' while unknown.
   */
  readonly dbInstanceId: string;
  /** How this device syncs. Absent in configurations written before accounts existed: `passphrase`. */
  readonly mode: SyncMode;
  /** Normalised email of the logged-in account; '' in passphrase mode. */
  readonly accountEmail: string;
  /**
   * The device joined a space that already existed on the server (login, passphrase join or enabling with an
   * existing passphrase). The first sync pulls before it pushes and the space's settings replace the device's
   * own (architecture-account §2.7). Cleared after that pull; absent (false) in older configurations.
   */
  readonly pullFirst: boolean;
  /**
   * The record types (sorted) of the newest build that synced with this configuration; `null` when the
   * configuration was written by a build without this field. A build that knows more types than the marker
   * lists pulls everything again from cursor 0 once, because the builds before it skipped records of the types
   * they did not know while moving the cursor on. A build whose own types are not a superset leaves the marker
   * as it is.
   */
  readonly knownTypes: readonly string[] | null;
  /**
   * The record type set (sorted) for which this configuration was already pulled again from cursor 0 by
   * `knownTypes`. An older tab may overwrite `knownTypes` with its smaller set; a build that already pulled
   * again for its own set does not do it a second time. `null` = never.
   */
  readonly rewoundTypes: readonly string[] | null;
}

/** What a new configuration may already know. */
export interface SyncIdentity {
  readonly epoch?: string;
  readonly dbInstanceId?: string;
  readonly mode?: SyncMode;
  readonly accountEmail?: string;
  readonly pullFirst?: boolean;
}

/** Synchronous, device-local storage of the configuration (a `localStorage` entry in the browser). */
export interface SyncConfigStore {
  load(): SyncConfig | null;
  save(config: SyncConfig): void;
  clear(): void;
}

export type KeyValueStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

export function newSyncConfig(serverUrl: string, spaceId: string, identity: SyncIdentity = {}): SyncConfig {
  return {
    serverUrl,
    spaceId,
    cursor: 0,
    lastPushedHlc: '',
    fullPushFrom: '',
    lastSyncAt: null,
    epoch: identity.epoch ?? '',
    dbInstanceId: identity.dbInstanceId ?? '',
    mode: identity.mode ?? SyncMode.Passphrase,
    accountEmail: identity.accountEmail ?? '',
    pullFirst: identity.pullFirst ?? false,
    knownTypes: null,
    rewoundTypes: null,
  };
}

/**
 * Make the next sync push every local record (after an import, say). The request is stored in the
 * configuration, so it holds while no sync engine is running; nothing happens while sync is not
 * configured. Pure with respect to everything but the given store.
 */
export function markFullPushPending(store: SyncConfigStore): void {
  const config = store.load();
  if (config !== null) store.save({ ...config, fullPushFrom: '' });
}

function isSyncMode(value: unknown): value is SyncMode {
  return value === SyncMode.Passphrase || value === SyncMode.Account;
}

/** A list of strings, or null when absent or malformed (the worst case is one more pull from cursor 0). */
function parseTypeList(value: unknown): readonly string[] | null {
  if (!Array.isArray(value) || !value.every((type) => typeof type === 'string')) return null;
  return [...(value as string[])];
}

function parseConfig(text: string): SyncConfig | null {
  let value: unknown;
  try {
    value = JSON.parse(text);
  } catch {
    return null;
  }
  if (typeof value !== 'object' || value === null) return null;
  const v = value as Record<string, unknown>;
  if (
    typeof v.serverUrl !== 'string' ||
    typeof v.spaceId !== 'string' ||
    !/^[0-9a-f]{32}$/.test(v.spaceId) ||
    typeof v.cursor !== 'number' ||
    !Number.isInteger(v.cursor) ||
    v.cursor < 0 ||
    typeof v.lastPushedHlc !== 'string' ||
    (v.fullPushFrom !== null && typeof v.fullPushFrom !== 'string') ||
    (v.lastSyncAt !== null && typeof v.lastSyncAt !== 'number') ||
    // Absent in configurations written before these fields existed.
    (v.epoch !== undefined && typeof v.epoch !== 'string') ||
    (v.dbInstanceId !== undefined && typeof v.dbInstanceId !== 'string') ||
    (v.mode !== undefined && !isSyncMode(v.mode)) ||
    (v.accountEmail !== undefined && typeof v.accountEmail !== 'string') ||
    (v.pullFirst !== undefined && typeof v.pullFirst !== 'boolean')
  ) {
    return null;
  }
  return {
    serverUrl: v.serverUrl,
    spaceId: v.spaceId,
    cursor: v.cursor,
    lastPushedHlc: v.lastPushedHlc,
    fullPushFrom: v.fullPushFrom,
    lastSyncAt: v.lastSyncAt,
    epoch: v.epoch ?? '',
    dbInstanceId: v.dbInstanceId ?? '',
    mode: v.mode ?? SyncMode.Passphrase,
    accountEmail: v.accountEmail ?? '',
    pullFirst: v.pullFirst ?? false,
    knownTypes: parseTypeList(v.knownTypes),
    rewoundTypes: parseTypeList(v.rewoundTypes),
  };
}

/** Configuration in a `localStorage`-like object; a corrupt entry reads as "sync not configured". */
export function createStorageConfigStore(
  storage?: KeyValueStorage,
  entryKey: string = SYNC_CONFIG_KEY,
): SyncConfigStore {
  const resolve = (): KeyValueStorage => storage ?? globalThis.localStorage;
  return {
    load() {
      const text = resolve().getItem(entryKey);
      return text === null ? null : parseConfig(text);
    },
    save(config) {
      resolve().setItem(entryKey, JSON.stringify(config));
    },
    clear() {
      resolve().removeItem(entryKey);
    },
  };
}

export function createMemoryConfigStore(initial: SyncConfig | null = null): SyncConfigStore {
  let current = initial;
  return {
    load: () => (current === null ? null : { ...current }),
    save: (config) => {
      current = { ...config };
    },
    clear: () => {
      current = null;
    },
  };
}

/**
 * The identifier of the space this device last synced with ('' when it never did). It outlives the sync
 * configuration (logout and disconnect keep it) so that the next activation can tell whether the data on
 * the device belongs to another space; it is cleared together with the local data.
 */
export interface LastSpaceStore {
  load(): string;
  /** Returns false when the value could not be stored (the guard then relies on the record device ids). */
  save(spaceId: string): boolean;
  clear(): void;
}

/** The last space in a `localStorage`-like object; unreadable or unwritable storage reads as '' and ignores writes. */
export function createStorageLastSpaceStore(
  storage?: KeyValueStorage,
  entryKey: string = LAST_SPACE_ID_KEY,
): LastSpaceStore {
  const resolve = (): KeyValueStorage => storage ?? globalThis.localStorage;
  return {
    load() {
      try {
        return resolve().getItem(entryKey) ?? '';
      } catch {
        return '';
      }
    },
    save(spaceId) {
      try {
        resolve().setItem(entryKey, spaceId);
        return true;
      } catch {
        // Sync itself is unaffected; the caller logs it and the guard falls back to the record device ids.
        return false;
      }
    },
    clear() {
      try {
        resolve().removeItem(entryKey);
      } catch {
        // See save.
      }
    },
  };
}

export function createMemoryLastSpaceStore(initial = ''): LastSpaceStore {
  let current = initial;
  return {
    load: () => current,
    save: (spaceId) => {
      current = spaceId;
      return true;
    },
    clear: () => {
      current = '';
    },
  };
}
